<template>
<div class="group overflow-hidden rounded-lg lg:rounded-[2rem] bg-card border border-app hover:border-blue-500/50 hover:shadow-2xl hover:shadow-blue-500/10 transition-all hover:-translate-y-2 flex flex-col">
    <div class="relative p-4 lg:p-8 flex-1 flex flex-col">
        <div
            class="absolute top-0 right-0 p-4 lg:p-6 z-10 transition-transform duration-300 group-hover:scale-110 origin-top-right">
            <div
                class="flex flex-col items-center justify-center min-w-[3.5rem] p-3 rounded-2xl bg-white/95 dark:bg-slate-800/95 shadow-sm backdrop-blur-md border border-black/5 dark:border-white/10">
                <span
                    class="text-blue-500 font-extrabold text-[10px] uppercase tracking-widest mb-1">{{
                        item.startDate ? getMonth(item.startDate) : 'TBD' }}</span>
                <span class="text-header font-black text-3xl leading-none">{{ item.startDate ?
                    getDayNum(item.startDate) : '-' }}</span>
            </div>
        </div>

        <div
            class="size-14 rounded-2xl bg-blue-500/10 flex items-center justify-center mb-8 text-blue-500 shadow-inner group-hover:bg-blue-500 group-hover:text-white transition-colors duration-300">
            <Calendar class="h-7 w-7" />
        </div>

        <h4 class="text-xl font-bold text-header mb-4 group-hover:text-blue-500 transition-colors line-clamp-2 leading-snug">
            {{ item.title }}</h4>
        <p class="text-body/80 text-sm leading-relaxed mb-8 line-clamp-3">{{ item.description }}</p>

        <div class="mt-auto flex flex-col gap-4 py-5 border-t border-app/50">
            <div class="flex items-center gap-3.5 text-sm group/item">
                <div
                    class="p-1.5 rounded-md bg-muted/10 text-body/80 group-hover/item:bg-blue-500/10 group-hover/item:text-blue-500 transition-colors">
                    <Clock class="h-4 w-4" />
                </div>
                <span class="text-header font-bold">{{ item.startDate ? formatTime(item.startDate)
                    : 'TBA' }} - {{ item.endDate ? formatTime(item.endDate) : 'TBA' }}</span>
            </div>
            <div class="flex items-start gap-3.5 text-sm group/item" v-if="item.location">
                <div
                    class="p-1.5 rounded-md bg-muted/10 text-body/80 group-hover/item:bg-blue-500/10 group-hover/item:text-blue-500 transition-colors">
                    <MapPin class="h-4 w-4" />
                </div>
                <span class="text-header font-bold line-clamp-2 mt-0.5 leading-snug">{{
                    item.location }}</span>
            </div>
        </div>
    </div>

    <div class="px-4 lg:px-8 py-5 bg-muted/5 border-t border-app/50 flex items-center justify-between">
        <div :class="[
            'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest shadow-sm',
            item.status === 'upcoming' || item.status === 'active' ? 'bg-blue-500 text-white' : 'bg-muted/10 text-body/80 border border-app'
        ]">
            {{ item.status || 'Scheduled' }}
        </div>
        <button
            class="text-sm font-bold text-blue-500 hover:text-blue-600 flex items-center gap-1 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">Details
            <ArrowRight class="h-4 w-4" />
        </button>
    </div>
</div>
</template>

<script lang="ts" setup>
import {
    Megaphone,
    Calendar,
    Target,
    LayoutGrid,
    MapPin,
    ArrowRight,
    Clock,
} from 'lucide-vue-next';

const props = defineProps<{
    item: Record<string, any>;
}>();


// Helper functions for dates since $filter.day might just return ISO or localized simple string.
const getMonth = (isoDate: string) => {
    if (!isoDate) return '';
    try {
        const d = new Date(isoDate);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleString('default', { month: 'short' });
    } catch (e) { return ''; }
}
const getDayNum = (isoDate: string) => {
    if (!isoDate) return '';
    try {
        const d = new Date(isoDate);
        if (isNaN(d.getTime())) return '';
        return d.getDate();
    } catch (e) { return ''; }
}
const formatTime = (isoDate: string) => {
    if (!isoDate) return '';
    try {
        const d = new Date(isoDate);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) { return ''; }
}
</script>

<style lang="scss" scoped>

</style>